import {Image, Platform} from 'react-native';
import {makeAutoObservable, runInAction} from 'mobx';

import * as RootNavigation from '../../app/navigation/RootNavigation';
import config from '../config';
import GiftCardsIconImg from '../images/brand/icons/category/gift-cards.png';
import SpecialsIconImg from '../images/brand/icons/category/specials.png';
import {Department} from '../models/middleware';
import apiService, {
  GetDepartmentLinesRequestParams,
} from '../transport/apiService';
import {OrderLine} from '../transport/apiServiceResponse';

import type {RootStore} from './rootStore';

const specialsIconUri =
  Platform.OS === 'web'
    ? SpecialsIconImg
    : Image.resolveAssetSource(SpecialsIconImg).uri;

const giftCardsIconUri =
  Platform.OS === 'web'
    ? GiftCardsIconImg
    : Image.resolveAssetSource(GiftCardsIconImg).uri;

export const SPECIALS_TILE = 'SPECIALS_TILE';
export const GIFT_CARDS_TILE = 'GIFT_CARDS_TILE';

export class RangeStore {
  rootStore: RootStore;
  // Department selection
  parentDepartment: Department | null = null;
  departments: Department[] = [];
  selectedDepartment: Department | null = null;
  selectedSubDepartment: Department | null = null;
  // Range product list
  departmentLines: OrderLine[] = [];
  departmentLinesMeta: any = {};
  // Filter & Sort options
  selectedSortOption: any = null;
  selectedFilterOptions: any = null;
  // Loading flags
  isLoadingDepartmentLines = false;
  isLoadingNextPage = false;

  // This is a specials tile
  specialsDepartment: Department = {
    id: SPECIALS_TILE,
    type: 'departments',
    name: 'Specials',
    parent: null,
    position: -5,
    promote_at_checkout: false,
    links: {
      icon: specialsIconUri,
    },
  };

  giftCardsDepartment: Department = {
    id: GIFT_CARDS_TILE,
    type: 'departments',
    name: 'Gift Cards',
    parent: null,
    position: -4,
    promote_at_checkout: false,
    links: {
      icon: giftCardsIconUri,
    },
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  reset = () => {
    this.parentDepartment = null;
    this.departments = [];
    this.selectedDepartment = null;
    this.departmentLines = [];
    this.departmentLinesMeta = {};
    this.selectedSubDepartment = null;
    this.selectedSortOption = {};
    this.selectedFilterOptions = null;
  };

  filterDepartmentsByParent = (parentId?: any) =>
    this.departments
      .slice()
      .sort((a: any, b: any) => a.position - b.position)
      .filter(
        (item: any) =>
          item.parent?.id === (parentId || this.parentDepartment?.id),
      );

  getDepartments = (parentId?: string) =>
    this.filterDepartmentsByParent(parentId);

  loadDepartments = async () => {
    try {
      const departments = await apiService.getDepartments();
      const parentDept = departments.filter(
        (item: any) => item.parent === null,
      )[0];
      const specials = {
        ...this.specialsDepartment,
        parent: {
          id: parentDept.id,
        },
      };
      const giftCards = {
        ...this.giftCardsDepartment,
        parent: {
          id: parentDept.id,
        },
      };
      const finalDepartments = [...departments, specials];
      if (config.FF_IS_GIFT_CARDS_ENABLED) {
        finalDepartments.push(giftCards);
      }
      runInAction(() => {
        this.parentDepartment = parentDept;
        // @ts-ignore
        this.departments = finalDepartments;
      });
    } catch (err) {
      console.log(err);
    }
  };

  selectDepartment = (department: Department) => {
    runInAction(() => {
      this.selectedDepartment = department;
    });
    if (department.id === SPECIALS_TILE) {
      apiService.setDepartment({specials: 1});
    } else {
      apiService.setDepartment({department_id: department.id});
    }
  };

  selectSubDepartment = (department: Department) => {
    runInAction(() => {
      this.selectedSubDepartment = department;
    });
    if (department.id === SPECIALS_TILE) {
      apiService.setDepartment({specials: 1});
    } else {
      apiService.setDepartment({department_id: department.id});
    }
  };

  getSubDepartments = (departmentId?: string) =>
    this.filterDepartmentsByParent(departmentId || this.selectedDepartment?.id);

  loadRangeLines = async () => {
    runInAction(() => {
      this.isLoadingDepartmentLines = true;
      this.departmentLines = [];
    });
    // sort options
    const options: GetDepartmentLinesRequestParams = {};
    if (this.selectedSortOption) {
      options.sort_option_id = this.selectedSortOption.id;
    }
    // filter options
    if (this.selectedFilterOptions) {
      options.facets = this.selectedFilterOptions;
    }

    try {
      const data = await apiService.getDepartmentLines(options);
      runInAction(() => {
        this.departmentLines = data.data;
        // @ts-ignore
        this.departmentLinesMeta = data.meta;
      });
    } catch (err) {
      console.log(err);
    }

    runInAction(() => {
      this.isLoadingDepartmentLines = false;
    });
  };

  loadNextPageRangeLines = () => {
    if (this.isLoadingNextPage) {
      return;
    }
    if (this.departmentLinesMeta.page < this.departmentLinesMeta.page_count) {
      this.isLoadingNextPage = true;
      const options: any = {};
      if (this.selectedSortOption) {
        options.sort_option_id = this.selectedSortOption.id;
      }
      options.page = this.departmentLinesMeta.page + 1;
      apiService
        .getDepartmentLines(options)
        .then((data: any) => {
          runInAction(() => {
            this.departmentLines = [...this.departmentLines, ...data.data];
            this.departmentLinesMeta = data.meta;
            this.isLoadingDepartmentLines = false;
            this.isLoadingNextPage = false;
          });
        })
        .catch(err => {
          runInAction(() => {
            this.isLoadingDepartmentLines = false;
            this.isLoadingNextPage = false;
          });
          console.log(err);
        });
    }
  };

  setSelectedSortOption = (option: any) => (this.selectedSortOption = option);

  setSelectedFilterOptions = (filters: any[]) =>
    (this.selectedFilterOptions = filters);

  clearSelectedFilterOptions = () => (this.selectedFilterOptions = null);

  clearSelectedSortAndFilter = (defaultSortOption: any) =>
    runInAction(() => {
      this.selectedSortOption = defaultSortOption;
      this.selectedFilterOptions = null;
    });
}
